import React, { useEffect, useState } from "react";
import { CheckCircleOutlined, FormOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { Alert, Steps } from "antd";
import { connect } from "dva";
import moment from "moment";
import { dateStringToStandardFormat } from "@/utils/dateFormatter";
import { ShipmentStatus, ShipmentStatusStep, QuoteStatus, LoadStatus } from "@/utils/enum";
import useScreenDetector from "@/hooks/useScreenDetector";
import DealerExchangeAlert from "@/components/DealerExchangeAlert";
import ETAWindow from "@/components/ETAWindow";
import { checkVehicleExpiredQuote } from "../VehicleListContainer/VehicleListContainer";

const { Step } = Steps;

const draftIcon = <FormOutlined />;
const deliveredIcon = <CheckCircleOutlined style={{ color: "#52C41A" }} />; // uses antd success validation green
const onHoldIcon = <PauseCircleOutlined />;
// canceled status shipments gray out all steps and show a warning message above steps component instead

const generateDraftDescription = shipmentData => {
  const formattedDate = dateStringToStandardFormat(shipmentData.creationTime, { showTime: true });

  return `Shipment created on ${formattedDate}`;
};

const generateQuoteSubTitle = shipmentData => {
  if (shipmentData.status < ShipmentStatus.Pending || shipmentData.vehicles.length === 0) {
    return;
  }
  const quotedVehicles = shipmentData.vehicles.filter(vehicle => {
    return vehicle?.quote?.status === QuoteStatus.Completed;
  });

  return `${quotedVehicles.length} / ${shipmentData.vehicles.length}`;
};

const generateQuoteDescription = (shipmentData, quoteData) => {
  if (shipmentData.status < ShipmentStatus.Pending) {
    return;
  }

  if (quoteData.mostRecentQuoteResolvedAt) {
    const formattedDate = moment
      .utc(quoteData.mostRecentQuoteResolvedAt)
      .locale("en")
      .local()
      .format("MM/DD/YY, h:mm A");

    let vehicleDetails = `${quoteData.mostRecentQuoteVehicleYear} ${quoteData.mostRecentQuoteVehicleMake} ${quoteData.mostRecentQuoteVehicleModel}`;

    return `Last Update: Quoted ${vehicleDetails} at $${quoteData.mostRecentResolvedQuotePrice} on ${formattedDate}`;
  }

  return "";
};

const generatePostedDescription = shipmentData => {
  if (shipmentData.status < ShipmentStatus.Posted) return;

  const formattedDate = dateStringToStandardFormat(shipmentData.postedAt, { showTime: true });

  return `Shipment posted on ${formattedDate}`;
};

const generateAssignedDescription = (shipmentData, loadLocationData) => {
  if (shipmentData.status < ShipmentStatus.Assigned) return;

  const transporterDriverName = shipmentData?.load?.userFullName;
  const droppedOffAt = shipmentData?.load?.droppedOffAt;
  const loadStatus = shipmentData?.load?.status;

  const pickupETA = shipmentData?.load?.pickupETA;
  const dropoffETA = shipmentData?.load?.dropOffETA;
  const pickupETATimeSlotIndex = shipmentData?.load?.pickupETATimeSlot;
  const dropoffETATimeSlotIndex = shipmentData?.load?.dropOffETATimeSlot;

  const formattedDropoffDate = dateStringToStandardFormat(droppedOffAt, { showTime: true });

  // Load is Assigned, but not in-transit
  if (loadStatus === LoadStatus.Active) {
    if (pickupETATimeSlotIndex >= 0 && pickupETATimeSlotIndex <= 2) {
      return (
        <ETAWindow
          etaDate={pickupETA}
          etaTimeSlotIndex={pickupETATimeSlotIndex}
          style={{ color: "rgba(0, 0, 0, 0.45)" }}
        />
      );
    }

    // if we don't have the above two bits of data, something went wrong
  }

  // Load has been picked up and is in-transit
  if (loadStatus === LoadStatus.PickedUp) {
    // will build node with fragments to return for description
    const descriptionFragments = [];

    if (dropoffETATimeSlotIndex >= 0 && dropoffETATimeSlotIndex <= 2) {
      descriptionFragments.push(
        <ETAWindow
          etaDate={dropoffETA}
          etaTimeSlotIndex={dropoffETATimeSlotIndex}
          style={{ color: "rgba(0, 0, 0, 0.45)" }}
        />,
      );
    }

    // check if we have a recent location update to display
    if (
      loadLocationData !== null &&
      loadLocationData !== undefined &&
      loadLocationData.loadTransportLog &&
      loadLocationData.address
    ) {
      const formattedLocationDate = dateStringToStandardFormat(loadLocationData?.loadTransportLog?.timestamp, {
        showTime: true,
      });

      if (
        shipmentData?.status !== ShipmentStatus.Delivered &&
        loadLocationData.address.city &&
        loadLocationData.address.stateCode &&
        formattedLocationDate
      ) {
        descriptionFragments.push(
          <div style={{ color: "rgba(0, 0, 0, 0.45)" }}>
            {`Last Update: ${loadLocationData.address.city}, ${loadLocationData.address.stateCode} on ${formattedLocationDate}`}
          </div>,
        );
      }
    }

    return descriptionFragments;
  }

  // Load has been dropped off
  if (loadStatus >= LoadStatus.Delivered) {
    // load dropped off, tell them who dropped it off

    if (transporterDriverName && formattedDropoffDate) {
      return `Dropped off by ${transporterDriverName} at ${formattedDropoffDate}`;
    }

    // if we don't have the above two bits of data, something went wrong
  }

  return "";
};

const generateAssignedSubTitle = shipmentData => {
  if (shipmentData.status === undefined || shipmentData.status < ShipmentStatus.Assigned) {
    return "";
  }

  let loadStatus = shipmentData?.load?.status;

  if (loadStatus === LoadStatus.Active) {
    return "Awaiting Pickup";
  }
};

const parseQuoteData = shipmentData => {
  if (shipmentData.status < ShipmentStatus.Pending) {
    return;
  }

  let mostRecentQuoteResolvedAt;
  let mostRecentResolvedQuotePrice;
  let mostRecentQuoteVehicleYear;
  let mostRecentQuoteVehicleMake;
  let mostRecentQuoteVehicleModel;
  let numVehiclesQuoted = 0;

  for (let vehicle of shipmentData.vehicles || []) {
    let quote = vehicle?.quote;

    // check if vehicle is quoted and quote is not expired
    const expired = checkVehicleExpiredQuote(vehicle);
    if (quote?.status === QuoteStatus.Completed && !expired) {
      numVehiclesQuoted++;

      // check if vehicle was quoted most recently
      if (
        !mostRecentQuoteResolvedAt ||
        moment(quote?.lastModificationTime).isAfter(moment(mostRecentQuoteResolvedAt))
      ) {
        mostRecentQuoteResolvedAt = quote?.lastModificationTime;
        mostRecentResolvedQuotePrice = quote?.dealerPrice;
        mostRecentQuoteVehicleYear = vehicle.year;
        mostRecentQuoteVehicleMake = vehicle.make;
        mostRecentQuoteVehicleModel = vehicle.model;
      }
    }
  }

  return {
    numVehiclesQuoted,
    mostRecentQuoteVehicleYear,
    mostRecentQuoteVehicleMake,
    mostRecentQuoteVehicleModel,
    mostRecentQuoteResolvedAt,
    mostRecentResolvedQuotePrice,
  };
};

const parseAssignedData = shipmentData => {
  if (shipmentData?.load?.status !== LoadStatus.PickedUp) return {};

  let pickedUpAt = moment.utc(shipmentData?.load?.pickedUpAt);
  let dropoffEta = moment.utc(shipmentData?.load?.dropOffETA);
  let currentTime = moment.utc();

  // let hoursSincePickup = currentTime.diff(pickedUpAt, 'hours');
  // let estimatedTripDurationInHours = dropoffEta.diff(pickedUpAt, 'hours');

  let minutesSincePickup = currentTime.diff(pickedUpAt, "minutes");
  let estimatedTripDurationInMinutes = dropoffEta.diff(pickedUpAt, "minutes");

  let estimateRemainingHours = dropoffEta.diff(currentTime, "hours");
  let estimateRemainingMinutes = dropoffEta.diff(currentTime, "minutes");
  let percentTripCompleted = Math.floor((minutesSincePickup / estimatedTripDurationInMinutes) * 100);

  let runningLate = currentTime.isAfter(dropoffEta.add(moment.duration(4, "h"), "hours"));
  let arrivingSoon = !runningLate && estimateRemainingMinutes <= 0;

  return {
    estimateRemainingHours,
    estimateRemainingMinutes,
    percentTripCompleted,
    isInTransit: true,
    runningLate,
    arrivingSoon,
  };
};

const generateTrackerData = (shipmentData, loadLocationData) => {
  // draft
  const draftSubTitle = "";
  const draftDescription = generateDraftDescription(shipmentData);

  // quote
  const quoteData = parseQuoteData(shipmentData);

  const quotesSubTitle = generateQuoteSubTitle(shipmentData);
  const quotesDescription = generateQuoteDescription(shipmentData, quoteData);
  const numOfVehicles = shipmentData.vehicles.length;
  const numVehiclesQuoted = shipmentData.vehicles.filter(vehicle => {
    return vehicle?.quote?.status === QuoteStatus.Completed;
  }).length;

  const quotesPercentCompleted = (numVehiclesQuoted / numOfVehicles) * 100;

  // posted
  const postedSubTitle = "";
  const postedDescription = generatePostedDescription(shipmentData);

  // assigned
  const assignedData = parseAssignedData(shipmentData);

  const assignedSubTitle = generateAssignedSubTitle(shipmentData);
  const assignedDescription = generateAssignedDescription(shipmentData, loadLocationData);

  // delivered
  const deliveredSubTitle = "";
  const deliveredDescription = "";

  const newTrackerData = {
    currentStep: ShipmentStatusStep[shipmentData.status],
    steps: [
      {
        title: "Draft",
        subTitle: draftSubTitle,
        description: draftDescription,
        icon: shipmentData.status === ShipmentStatus.Draft && draftIcon,
      },
      {
        title: quotesPercentCompleted === 100 ? "Quoted" : "Pending Quotes",
        subTitle: quotesSubTitle,
        description: quotesDescription,
        percentCompleted: quotesPercentCompleted,
      },
      {
        title: "Posted",
        subTitle: postedSubTitle,
        description: postedDescription,
      },
      {
        title: assignedData.isInTransit ? "In-Transit" : "Assigned",
        subTitle: assignedSubTitle,
        description: assignedDescription,
      },
      {
        title: "Delivered",
        subTitle: deliveredSubTitle,
        description: deliveredDescription,
        icon: shipmentData.status === ShipmentStatus.Delivered && deliveredIcon,
      },
    ],
  };

  // change current step text and icon if shipment is on hold
  if (shipmentData.onHold) {
    newTrackerData.steps[newTrackerData.currentStep] = {
      title: "On Hold",
      subTitle: "",
      description: "",
      icon: onHoldIcon,
    };
  }

  return newTrackerData;
};

const ShipmentStatusTracker = ({ shipmentData, loadLocationData }) => {
  const { isMobile } = useScreenDetector(1100);

  const [trackerData, setTrackerData] = useState({
    currentStep: ShipmentStatus.Draft,
    steps: [
      {
        title: "Draft",
        subTitle: "",
        description: "",
      },
      {
        title: "Pending Quotes",
        subTitle: "",
        description: "",
        percentCompleted: 0,
      },
      {
        title: "Posted",
        subTitle: "",
        description: "",
      },
      {
        title: "Assigned",
        subTitle: "",
        description: "",
        percentCompleted: 0,
      },
      {
        title: "Delivered",
        subTitle: "",
        description: "",
      },
    ],
  });

  useEffect(() => {
    const data = generateTrackerData(shipmentData, loadLocationData);

    setTrackerData(data);
  }, [shipmentData, loadLocationData]);

  return (
    <>
      {shipmentData.status === ShipmentStatus.Incomplete && (
        <Alert message="This shipment has been cancelled." type="error" showIcon style={{ marginBottom: "24px" }} />
      )}
      {shipmentData.onHold && (
        <Alert
          message="This shipment is pending review by Autosled."
          type="warning"
          showIcon
          style={{ marginBottom: "24px" }}
        />
      )}
      {shipmentData.isInternalShipment && (
        <Alert
          message="This shipment has been marked as internal."
          type="info"
          showIcon
          style={{ marginBottom: "24px" }}
        />
      )}
      {shipmentData.childShipmentId && (
        <DealerExchangeAlert
          relativeUrl={"/shipments/dealer/all/view"}
          shipmentId={shipmentData.childShipmentId}
          exchangeType={shipmentData.dealerExchangeType}
          isRequired={shipmentData.isExchangeRequired}
          style={{ marginBottom: "24px" }}
        />
      )}
      {shipmentData.parentShipmentId && (
        <DealerExchangeAlert
          isChild
          relativeUrl={"/shipments/dealer/all/view"}
          shipmentId={shipmentData.parentShipmentId}
          exchangeType={shipmentData.dealerExchangeType}
          isRequired={shipmentData.isExchangeRequired}
          style={{ marginBottom: "24px" }}
        />
      )}

      <Steps
        size="small"
        direction={isMobile ? "vertical" : "horizontal"}
        current={trackerData.currentStep}
        percent={trackerData.steps[trackerData.currentStep]?.percentCompleted}
        className="test"
      >
        {trackerData.steps.map((step, index) => (
          <Step
            key={index}
            {...step}
            status={shipmentData?.status === ShipmentStatus.Incomplete ? "wait" : undefined}
          />
        ))}
      </Steps>
    </>
  );
};

export default connect(({ shipmentDetails }) => ({
  shipmentData: shipmentDetails.data,
  loadLocationData: shipmentDetails.loadLocationData,
}))(ShipmentStatusTracker);
